import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { BrandAsset } from '../../services/editingApi';
import type { AngleKey, EditList } from '../../utils/editing/editList';
import { angleAt, focusAt } from '../../utils/editing/editList';
import { setFocusKey } from '../../utils/media/faceTrack';
import type { PreviewPlayer } from './usePreviewPlayer';
import PreviewOverlay, { type OverlaySelection, type PreviewCue, outputAspectRatio, reframeWindow } from './PreviewOverlay';
import { isReadableAssetUrl } from './usePreviewAudio';

/**
 * The stage: a canvas in the export's own shape, showing exactly what the export
 * keeps. A 9:16 Short is a vertical frame; the source is placed behind it so the
 * crop window fills it. "Position crop" shows the whole source instead, darkened
 * outside a window the creator drags to choose what stays in frame.
 */

interface StageCanvasProps {
  list: EditList;
  player: PreviewPlayer;
  videoUrl: string;
  assets: BrandAsset[];
  cues: PreviewCue[];
  /** Show the whole source with a draggable crop window. */
  positionCrop: boolean;
  /** The canvas's height budget, in px: the studio works it out so the timeline stays on screen. */
  maxHeight: number;
  selected: OverlaySelection;
  onSelect: (selection: OverlaySelection) => void;
  /** Live changes from dragging (pictures, text, the crop window), coalesced by key. */
  onChange: (next: EditList, key: string) => void;
  /** A multicam set's angles: the preview shows the one each camera cut picks, in step with the master. */
  angles?: Array<{ angleKey: string; videoUrl: string; timelineStartMs: number; sourceInMs: number; sourceOutMs: number | null; durationMs: number }>;
  masterKey?: string;
}

/** Where an angle's file is at a time on the set's timeline, or null where it has no footage. */
export function angleFileMs(angle: { timelineStartMs: number; sourceInMs: number; sourceOutMs: number | null; durationMs: number }, timelineMs: number): number | null {
  const fileMs = timelineMs - angle.timelineStartMs + (angle.sourceInMs || 0);
  const end = angle.sourceOutMs && angle.sourceOutMs > (angle.sourceInMs || 0) ? angle.sourceOutMs : angle.durationMs > 0 ? angle.durationMs : Infinity;
  if (fileMs < (angle.sourceInMs || 0) || fileMs >= end) return null;
  return fileMs;
}

const fit = (aspect: number, width: number, height: number): { width: number; height: number } => {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  if (width / height > aspect) return { width: Math.round(height * aspect), height: Math.round(height) };
  return { width: Math.round(width), height: Math.round(width / aspect) };
};

const StageCanvas: React.FC<StageCanvasProps> = ({ list, player, videoUrl, assets, cues, positionCrop, maxHeight, selected, onSelect, onChange, angles, masterKey = 'A' }) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const angleVideos = useRef<Record<string, HTMLVideoElement | null>>({});
  // Multicam: the angle the cut shows now (the master plays the clock and the sound underneath).
  const timelineMs = player.sourceMs;
  const wanted = angles && angles.length > 1 ? angleAt(list.multicamCut, timelineMs, masterKey as AngleKey) : masterKey;
  const shownAngle = angles && wanted !== masterKey ? angles.find((angle) => angle.angleKey === wanted && angleFileMs(angle, timelineMs) !== null) || null : null;
  useEffect(() => {
    if (!angles) return;
    angles.forEach((angle) => {
      const el = angleVideos.current[angle.angleKey];
      if (!el) return;
      if (!shownAngle || shownAngle.angleKey !== angle.angleKey) {
        if (!el.paused) el.pause();
        return;
      }
      const at = angleFileMs(angle, timelineMs);
      if (at === null) return;
      if (Math.abs(el.currentTime * 1000 - at) > 250) el.currentTime = at / 1000;
      if (player.element) el.playbackRate = player.element.playbackRate;
      if (player.playing && el.paused) el.play().catch(() => undefined);
      if (!player.playing && !el.paused) el.pause();
    });
  }, [angles, player.element, player.playing, shownAngle, timelineMs]);
  const [available, setAvailable] = useState({ width: 0, height: 0 });
  const dragRef = useRef<{ startX: number; startY: number; left: number; top: number; key: string; atMs: number } | null>(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => setAvailable({ width: el.clientWidth, height: maxHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('resize', measure);
    return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [maxHeight]);

  const sourceAspect = player.videoWidth && player.videoHeight ? player.videoWidth / player.videoHeight : 16 / 9;
  // A crop that follows the person moves with the picture: the window at this moment.
  const cropWindow = reframeWindow(list, player.videoWidth, player.videoHeight, timelineMs);
  const following = Boolean(list.reframe && list.reframe.track && list.reframe.track.length > 0);
  const aspect = positionCrop ? sourceAspect : outputAspectRatio(list, player.videoWidth, player.videoHeight);
  const canvas = fit(aspect, available.width, available.height);
  const padded = !positionCrop && list.output.aspect !== 'source' && list.reframe?.mode === 'pad';

  // The source behind the canvas: cropped (scaled up and offset so the window fills it), padded, or whole.
  let videoStyle: React.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' };
  if (!positionCrop && cropWindow && canvas.width > 0) {
    const width = canvas.width / cropWindow.width;
    const height = canvas.height / cropWindow.height;
    videoStyle = { position: 'absolute', width, height, left: -cropWindow.left * width, top: -cropWindow.top * height, maxWidth: 'none', display: 'block', objectFit: 'fill' };
  } else if (padded) {
    videoStyle = { ...videoStyle, objectFit: 'contain' };
  }

  // Dragging the crop window in "Position crop".
  const startCropDrag = (event: React.PointerEvent) => {
    if (!cropWindow) return;
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startY: event.clientY, left: cropWindow.left, top: cropWindow.top, key: `crop-${Date.now()}`, atMs: timelineMs };
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  };
  const moveCropDrag = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || !cropWindow || canvas.width === 0) return;
    const left = Math.min(Math.max(drag.left + (event.clientX - drag.startX) / canvas.width, 0), 1 - cropWindow.width);
    const top = Math.min(Math.max(drag.top + (event.clientY - drag.startY) / canvas.height, 0), 1 - cropWindow.height);
    const focusXPct = Math.round((left + cropWindow.width / 2) * 1000) / 10;
    const focusYPct = Math.round((top + cropWindow.height / 2) * 1000) / 10;
    const reframe = list.reframe || { mode: 'crop' as const, focusXPct: 50, focusYPct: 50, track: [] };
    // Following: the drag moves the crop at this moment only (a keyframe); otherwise the one position.
    onChange({ ...list, reframe: following ? { ...reframe, track: setFocusKey(reframe.track, drag.atMs, focusXPct, focusYPct) } : { ...reframe, focusXPct, focusYPct } }, drag.key);
  };
  const endCropDrag = (event: React.PointerEvent) => {
    dragRef.current = null;
    (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);
  };

  // A pause while dragging on the picture keeps the frame still under the pointer.
  useEffect(() => {
    if (positionCrop && player.playing) player.pause();
  }, [positionCrop, player]);

  return (
    <Box ref={boxRef} sx={{ width: '100%', height: available.height || 'auto', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="stage">
      <Box
        data-testid="stage-canvas"
        data-aspect={Math.round(aspect * 1000) / 1000}
        sx={{ position: 'relative', width: canvas.width || '100%', height: canvas.height || 'auto', bgcolor: '#000', overflow: 'hidden', borderRadius: '6px', boxShadow: '0 0 0 1px rgba(255,255,255,0.08)' }}
      >
        {/* Captions are drawn by the overlay layer, from the same cues the export uses. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={player.ref}
          src={videoUrl}
          crossOrigin={isReadableAssetUrl(videoUrl) ? 'anonymous' : undefined}
          playsInline
          preload="auto"
          style={videoStyle}
          data-testid="preview-video"
        />
        {angles && angles.length > 1 ? angles.filter((angle) => angle.angleKey !== masterKey).map((angle) => (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            key={angle.angleKey}
            ref={(node) => { angleVideos.current[angle.angleKey] = node; }}
            src={angle.videoUrl}
            muted
            playsInline
            preload="auto"
            style={{ ...videoStyle, visibility: shownAngle && shownAngle.angleKey === angle.angleKey ? 'visible' : 'hidden' }}
            data-testid={`preview-angle-${angle.angleKey}`}
          />
        )) : null}
        {angles && angles.length > 1 ? (
          <Typography variant="caption" sx={{ position: 'absolute', left: 8, top: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', px: 0.75, borderRadius: 1, pointerEvents: 'none' }} data-testid="preview-angle-badge">
            Camera {shownAngle ? shownAngle.angleKey : masterKey}
          </Typography>
        ) : null}
        {positionCrop ? (
          cropWindow ? (
            <Box
              data-testid="crop-window"
              role="slider"
              aria-label="What stays in frame"
              aria-valuenow={Math.round(list.reframe ? focusAt(list.reframe, timelineMs).focusXPct : 50)}
              onPointerDown={startCropDrag}
              onPointerMove={moveCropDrag}
              onPointerUp={endCropDrag}
              sx={{
                position: 'absolute',
                left: `${cropWindow.left * 100}%`,
                top: `${cropWindow.top * 100}%`,
                width: `${cropWindow.width * 100}%`,
                height: `${cropWindow.height * 100}%`,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
                border: '2px solid #FFFFFF',
                cursor: 'grab',
                touchAction: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <Typography variant="caption" sx={{ position: 'absolute', left: 6, top: 6, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', px: 0.75, borderRadius: 1 }}>
                {following ? 'Following the person · drag to change this moment' : 'Drag to choose what stays in frame'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ position: 'absolute', left: 8, bottom: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', px: 1, borderRadius: 1 }}>
              This shape keeps the whole picture.
            </Typography>
          )
        ) : (
          <PreviewOverlay list={list} outputMs={player.outputMs} assets={assets} cues={cues} editable={!player.playing} selected={selected} onSelect={onSelect} onChange={onChange} />
        )}
      </Box>
    </Box>
  );
};

export default StageCanvas;
